import { useCallback, useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const getIsStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (window.navigator as Navigator & { standalone?: boolean }).standalone ||
  document.referrer.includes('android-app://');

export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallAvailable, setIsInstallAvailable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(getIsStandalone);

  // Mobile detection
  const isMobile = useCallback(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const mobileKeywords = ['android', 'iphone', 'ipad', 'ipod', 'mobile', 'webos', 'blackberry'];
    return mobileKeywords.some(keyword => userAgent.includes(keyword)) ||
           /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent) ||
           (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);
  }, []);

  // iOS detection
  const isIOS = useCallback(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
  }, []);

  // Safari on mobile detection
  const isSafari = useCallback(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /safari/.test(userAgent) && !/chrome/.test(userAgent) && !/chromium/.test(userAgent);
  }, []);

  // Check if app is already installed
  const checkIfInstalled = useCallback(() => {
    return getIsStandalone();
  }, []);

  useEffect(() => {
    // Only show on mobile devices and if not already installed
    if (!isMobile() || checkIfInstalled()) {
      return;
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const installEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(installEvent);
      setIsInstallAvailable(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallAvailable(false);
      setDeferredPrompt(null);
    };

    // Listen for the beforeinstallprompt event (Chrome/Edge on Android)
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Listen for the appinstalled event
    window.addEventListener('appinstalled', handleAppInstalled);

    // For iOS Safari, show install option after a delay if not already installed
    if (isIOS() && !checkIfInstalled()) {
      const timer = setTimeout(() => {
        setIsInstallAvailable(true);
      }, 3000); // Show after 3 seconds on iOS

      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.removeEventListener('appinstalled', handleAppInstalled);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isMobile, isIOS, checkIfInstalled]);

  const promptInstall = useCallback(async () => {
    if (deferredPrompt) {
      // Use the deferred prompt for Chrome/Edge
      try {
        await deferredPrompt.prompt();
        const choiceResult = await deferredPrompt.userChoice;
        
        if (choiceResult.outcome === 'accepted') {
          setIsInstallAvailable(false);
        }
        
        setDeferredPrompt(null);
        return { success: true, outcome: choiceResult.outcome };
      } catch (error) {
        console.error('Error showing install prompt:', error);
        return { success: false, error };
      }
    }
    return { success: false, error: 'No install prompt available' };
  }, [deferredPrompt]);

  return {
    // Status
    isInstallAvailable: isInstallAvailable && isMobile() && !isInstalled,
    isInstalled,
    isIOS: isIOS(),
    isSafari: isSafari(),
    isMobile: isMobile(),
    canUseNativePrompt: !!deferredPrompt,
    
    // Actions
    promptInstall
  };
} 
